import React, { FunctionComponent, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { Formik } from 'formik';
import { Box, Button, Grid } from '@material-ui/core';
import { entries, sortBy } from 'lodash';

import { TrainingSettingsTypeEnum } from '@halter-corp/training-service-client';
import {
  ICattleCowgSettingsDTO,
  ICowgBreakToBreakSettingsDTO,
  ICowgEnterPaddockSettingsDTO,
  ICowgEnterSharedBoundarySettingsDTO,
  ICowgExitPaddockDraftingSettingsDTO,
  ICowgExitPaddockSettingsDTO,
  ICowgHoldInZoneSettingsDTO,
  ICowgRaceNavigationSettingsDTO,
} from '@halter-corp/settings-service-client';

import SettingsService from 'services/settings.service';
import { Profile } from 'data/training';
import { CattleCowgSettingsMetadata, SettingsMetadataProps } from 'data/settings';

import { ProfileFormValues } from './profile.form.types';
import { prettyPrintProfileType } from '../../utils';
import InputField from '../components/input-field';
import SelectField from '../components/select-field';
import SettingsInputTable from './components/settings-input-table';
import { deriveInitialProfileValues, getDefaults } from './utils';
import { profileTypeToCowgSettingsType } from './constants';
import { AnyCowgSettingsType } from './settings.types';

type ProfileFormProps = {
  onSubmit: (values: any) => void;
  isSubmitting?: boolean;
  initialProfile?: Profile;
};

const ProfileForm: FunctionComponent<ProfileFormProps> = ({
  onSubmit,
  isSubmitting = false,
  initialProfile,
}) => {
  const history = useHistory();

  const settingsMetadata = SettingsService.useCowgSettingsMetadata();

  const cowgSettingsMetadata = useMemo(
    () => (settingsMetadata?.cattleCowg ?? {}) as Partial<CattleCowgSettingsMetadata>,
    [settingsMetadata]
  );

  const defaults = useMemo(
    () =>
      entries(cowgSettingsMetadata).reduce((previous, [key, value]) => {
        if (value == null) return previous;

        return { ...previous, [key]: getDefaults<AnyCowgSettingsType>(value) };
      }, {}) as ICattleCowgSettingsDTO,
    [cowgSettingsMetadata]
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const initialValues = useMemo(
    () => deriveInitialProfileValues({ profile: initialProfile, defaults }),
    [defaults]
  );

  // Handlers
  //-----------------------------------------------------

  const handleProfileSubmit = (values: ProfileFormValues) => {
    onSubmit({ ...values });
  };

  // Render
  //-----------------------------------------------------

  return (
    <>
      {initialValues.settings != null && (
        <Formik<ProfileFormValues> initialValues={initialValues} onSubmit={handleProfileSubmit}>
          {({ handleChange, setFieldValue, handleSubmit, values }) => (
            <Box>
              <Grid container spacing={4}>
                <Grid item xs={12} md={6}>
                  <InputField
                    label="Name"
                    value={values.name}
                    onChange={handleChange('name')}
                    withMargin={false}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <SelectField
                    label="Profile type"
                    options={Object.values(TrainingSettingsTypeEnum).filter(
                      (type) => type !== TrainingSettingsTypeEnum.SYSTEM
                    )}
                    value={values.type}
                    valueExtractor={(value) => value}
                    displayNameExtractor={prettyPrintProfileType}
                    onChange={(type) => {
                      const settingsKey = profileTypeToCowgSettingsType[type];
                      setFieldValue('type', type);
                      setFieldValue('settings', settingsKey != null ? defaults[settingsKey] : undefined);
                    }}
                    withMargin={false}
                    disabled={initialProfile != null}
                  />
                </Grid>
              </Grid>

              {/* Farm settings */}
              <Box mt={6}>
                <SettingsInputTable
                  mode="profile"
                  // eslint-disable-next-line no-use-before-define
                  rows={getCowgSettingsEntriesOrder(cowgSettingsMetadata, values.type).map(
                    ([key, metaData]) => ({
                      key,
                      metaData: metaData as any,
                      value: values.settings[key],
                      onChange: (value) => setFieldValue(`settings.${key}`, value),
                    })
                  )}
                />
              </Box>
              <Box mt={6}>
                <Grid container spacing={2}>
                  <Grid item>
                    <Button onClick={history.goBack} disabled={isSubmitting} size="large" variant="outlined">
                      Cancel
                    </Button>
                  </Grid>
                  <Grid item>
                    <Button
                      onClick={() => handleSubmit()}
                      disabled={isSubmitting}
                      color="primary"
                      size="large"
                      variant="contained"
                    >
                      Save
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            </Box>
          )}
        </Formik>
      )}
    </>
  );
};

const HOLD_IN_ZONE_KEY_ORDER: (keyof ICowgHoldInZoneSettingsDTO)[] = [
  'enablePiezo',
  'disablePiezoRampReset',
  'enableVibe',
  'forwardIntensityRampUpTimeMs',
  'forwardIntensityRampUpDelayTimeMs',
  'shove',
  'forwardAngle',
  'dynamicForwardAngle',
  'naughtySettings',
  'enableNudge',
  'actuationRampUpTimeMs',
  'actuationRampUpDistanceM',
  'cowgScalars',
  'shockEnergyMj',
  'shockPulseMin',
  'shockLockout',
  'shockLockoutV2',
  'breakOutDistanceM',
  'gentleSettings',
];

type TransitionSettings = ICowgEnterPaddockSettingsDTO &
  ICowgExitPaddockSettingsDTO &
  ICowgBreakToBreakSettingsDTO &
  ICowgEnterSharedBoundarySettingsDTO &
  ICowgRaceNavigationSettingsDTO &
  ICowgExitPaddockDraftingSettingsDTO;

const TRANSITIONING_KEY_ORDER: (keyof TransitionSettings)[] = [
  'enablePiezo',
  'enableVibe',
  'forwardIntensityRampUpTimeMs',
  'forwardIntensityRampUpDelayTimeMs',
  'disableVibeOnMovement',
  'shove',
  'forwardAngleMin',
  'complexPaddockSettings',
  'actuationRampUpTimeMs',
  'actuationRampUpDistanceM',
  'cowgScalars',
  'shockEnergyMj',
  'shockPulseMin',
  'shockLockout',
  'shockLockoutV2',
  'gentleTransitionSettings',
];

const getCowgSettingsEntriesOrder = (
  cowgSettingsMetadata: Partial<CattleCowgSettingsMetadata>,
  type: TrainingSettingsTypeEnum
): Array<[string, SettingsMetadataProps<string | number | boolean | object | undefined>]> => {
  const cowgSettingsKey = profileTypeToCowgSettingsType[type];
  if (cowgSettingsKey == null) {
    return [];
  }

  switch (type) {
    case TrainingSettingsTypeEnum.HOLD_IN_ZONE:
    case TrainingSettingsTypeEnum.HOLD_IN_ZONE_CROP:
      return sortBy(
        entries(cowgSettingsMetadata[cowgSettingsKey] ?? {}),
        ([key]) => HOLD_IN_ZONE_KEY_ORDER.indexOf(key as keyof ICowgHoldInZoneSettingsDTO) ?? Infinity
      );
    case TrainingSettingsTypeEnum.ENTER_PADDOCK:
    case TrainingSettingsTypeEnum.EXIT_PADDOCK:
    case TrainingSettingsTypeEnum.EXIT_PADDOCK_DRAFTING:
    case TrainingSettingsTypeEnum.BREAK_TO_BREAK:
    case TrainingSettingsTypeEnum.RACE_NAVIGATION:
    case TrainingSettingsTypeEnum.BREAK_TO_BREAK_CROP:
    case TrainingSettingsTypeEnum.EXIT_PADDOCK_CROP:
    case TrainingSettingsTypeEnum.SHARED_BOUNDARY_TRANSITION:
    case TrainingSettingsTypeEnum.ENTER_SHARED_BOUNDARY:
    case TrainingSettingsTypeEnum.ENTER_SHARED_BOUNDARY_CROP:
    case TrainingSettingsTypeEnum.SHARED_BOUNDARY_TRANSITION_CROP:
    case TrainingSettingsTypeEnum.ENTER_PADDOCK_CROP:
      return sortBy(
        entries(cowgSettingsMetadata[cowgSettingsKey] ?? {}),
        ([key]) => TRANSITIONING_KEY_ORDER.indexOf(key as keyof TransitionSettings) ?? Infinity
      );
    default:
      return entries(cowgSettingsMetadata[cowgSettingsKey] ?? {});
  }
};

export default ProfileForm;
